import * as THREE from 'three';

import { getTerrainTexturePath, type TerrainTextureKey } from '../../../terrain/registry';

interface TextureRecord {
  texture: THREE.Texture | null;
  loaded: boolean;
  failed: boolean;
  loading: boolean;
}

export class TerrainTextureRegistry {
  private readonly loader = new THREE.TextureLoader();
  private readonly records = new Map<TerrainTextureKey, TextureRecord>();
  private readonly listeners = new Set<() => void>();

  public getTexture(key: TerrainTextureKey): THREE.Texture | null {
    const record = this.ensureRecord(key);
    if (!record || !record.loaded || record.failed || !record.texture) {
      return null;
    }
    return record.texture;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public dispose(): void {
    for (const record of this.records.values()) {
      record.texture?.dispose();
    }
    this.records.clear();
    this.listeners.clear();
  }

  private ensureRecord(key: TerrainTextureKey): TextureRecord | null {
    const existing = this.records.get(key);
    if (existing) {
      return existing;
    }

    const record: TextureRecord = {
      texture: null,
      loaded: false,
      failed: false,
      loading: true,
    };
    this.records.set(key, record);

    this.loader.load(
      getTerrainTexturePath(key),
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestMipmapNearestFilter;
        texture.needsUpdate = true;
        record.texture = texture;
        record.loaded = true;
        record.loading = false;
        this.notifyListeners();
      },
      undefined,
      () => {
        record.failed = true;
        record.loading = false;
      },
    );

    return record;
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
